import { alpha, ThemeOptions } from '@mui/material';
import type {} from '@mui/x-data-grid/themeAugmentation';

// Neutral scale from the Nimble Energy design system.
const grey = {
  50: '#FBFAF7',
  100: '#F1EFEA',
  200: '#D9DCDE',
  300: '#B6C0C6',
  400: '#8C979E',
  500: '#7C868E',
  600: '#5E6972',
  700: '#4A5660',
  800: '#34424C',
  900: '#17222B',
  A100: '#F1EFEA',
  A200: '#D9DCDE',
  A400: '#8C979E',
  A700: '#5E6972',
};

const luzmoBackgroundColor = 'rgb(242, 242, 242)';
const formControl = '#4d4d4d';

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    common: { black: '#000', white: '#fff' },
    grey,
    primary: {
      light: '#4FD1B8',
      main: '#0A6B5E',
      dark: '#08564C',
      contrastText: '#fff',
    },
    secondary: {
      main: '#F2B233',
      contrastText: '#17222B',
    },
    info: { light: '#8CC4F5', main: '#1F5F99', contrastText: '#fff' },
    success: { light: '#6FD18A', main: '#1E6B34', contrastText: '#fff' },
    error: { light: '#FF8A80', main: '#B3261E', contrastText: '#fff' },
    warning: { light: '#F2B233', main: '#8A5300', contrastText: '#fff' },
    text: {
      primary: grey[900],
      secondary: grey[700],
    },
    // Disabled colors match the design's disabled button; hover, selected and
    // focus keep MUI's default opacities but tint with the design's ink color.
    action: {
      active: grey[700],
      hover: alpha(grey[900], 0.04),
      selected: alpha(grey[900], 0.08),
      focus: alpha(grey[900], 0.12),
      disabled: grey[600],
      disabledBackground: grey[100],
    },
    background: {
      paper: '#fff',
      default: luzmoBackgroundColor,
    },
  },
  typography: {
    fontFamily: '"Fira Sans", sans-serif',
    h1: { fontSize: '3rem', lineHeight: 53 / 48, fontWeight: 700 },
    h2: { fontSize: '2rem', lineHeight: 37 / 32, fontWeight: 700 },
    h3: { fontSize: '1.5rem', lineHeight: 29 / 24, fontWeight: 600 },
    h4: { fontSize: '1.1875rem', lineHeight: 25 / 19, fontWeight: 600 },
    body1: { fontSize: '1rem', lineHeight: 25 / 16 },
    body2: { fontSize: '0.875rem', lineHeight: 21 / 14 },
    caption: {
      color: grey[800],
    },
  },
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          overflow: 'visible',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-mainContent': {
            height: '100%', // Ensures Safari table rows fill page height.
          },
          '& .MuiDataGrid-pinnedColumnHeaders': {
            paddingRight: '0px !important', // Remove the padding-right in Chrome browsers
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-cell--editing .MuiInputBase-root': {
            backgroundColor: grey[200],
            width: '100%',
            borderRadius: '4px',
          },
          '& .MuiDataGrid-row--editing .MuiDataGrid-cell--editing input': {
            textAlign: 'center',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-shrink': {
            color: formControl,
          },
        },
      },
    },
  },
};

export default themeOptions;
